import * as d3 from 'd3';
import React, { useEffect, useRef, useState } from 'react';
import {
  DAY_NAMES,
  generateYearObject,
  getColorFill,
  MONTH_NAMES,
  MONTH_ORDER,
  WEEKS_ORDER,
} from './utils/index.js';
import {
  createTooltipsReportEnterprise,
  createTooltipsReportProject,
} from './utils/create-tooltips.jsx';

const ContributionCalendar = ({
  query,
  data = [],
  reportType,
  shownDataPeriod,
  setIsShownImpacts,
  setShownImpacts,
  isLastShownYear,
  shownImpactsNumber,
  project,
  setShownImpactsNumber,
  maxNumberImpactsDay,
  setMaxNumberImpactsDay,
}) => {
  const [shownData, setShownData] = useState(null);
  const [size, setSize] = useState([0, 0]);

  const svgRef = useRef(null);
  let cellSize = 14;
  const spacing = 1;

  const osType = window.navigator.platform;
  const screenWidth =
    window.innerWidth ||
    document.documentElement.clientWidth ||
    document.body.clientWidth;

  const updateCellSize = (screenWidth - 12 * 5 * 2 - 54) / (5 * 12);
  if (updateCellSize > cellSize) {
    if (osType && osType.toLowerCase().includes('win')) {
      cellSize = updateCellSize - 0.2;
    } else {
      cellSize = updateCellSize;
    }
  }

  useEffect(() => {
    if (data) {
      const updatedYearsStructure = {};

      const YEARS_STRUCTURE = generateYearObject(
        shownDataPeriod.year,
        shownDataPeriod.start,
        shownDataPeriod.end
      );

      MONTH_ORDER.forEach((month) => {
        const updateMonth = YEARS_STRUCTURE[month].map((week) => {
          const updateWeek = week.map((day, index) => {
            if (day && index < 5) {
              const dateToCheck = new Date(day.date);
              const dateToCheckDateOnly = new Date(
                dateToCheck.getFullYear(),
                dateToCheck.getMonth(),
                dateToCheck.getDate()
              );

              const impactsOnDate = data.filter((impact) => {
                const startDate = new Date(impact.startDate);
                const endDate = new Date(impact.endDate);

                const startDateDateOnly = new Date(
                  startDate.getFullYear(),
                  startDate.getMonth(),
                  startDate.getDate()
                );
                const endDateDateOnly = new Date(
                  endDate.getFullYear(),
                  endDate.getMonth(),
                  endDate.getDate()
                );

                return (
                  startDateDateOnly <= dateToCheckDateOnly &&
                  dateToCheckDateOnly <= endDateDateOnly
                );
              });
              if (day.enabled && impactsOnDate?.length) {
                setMaxNumberImpactsDay((prev) =>
                  prev < impactsOnDate.length ? impactsOnDate.length : prev
                );
              }

              return {
                ...day,
                impacts: day.enabled ? impactsOnDate : [],
              };
            }
            return null;
          });
          return updateWeek;
        });
        updatedYearsStructure[month] = updateMonth;
      });
      setShownData(updatedYearsStructure);
    }
  }, [shownDataPeriod, data, query]);

  const handleMouseEvents = (d, isMouseOver, xPosition1, yPosition1) => {
    const data = d;

    let leftEdge = false;
    let rightEdge = false;

    let xPosition = xPosition1;
    let yPosition = yPosition1;

    if (xPosition < 150) {
      xPosition += 25;
      yPosition -= 15;
      leftEdge = true;
    }

    if (size[0] - xPosition - 48 < 150) {
      yPosition -= 25;
      rightEdge = true;
    }

    const { contentTooltip, contentEmptyTooltip } =
      createTooltipsReportProject(data);
    const { contentTooltipEnterprise, contentEmptyTooltipEnterprise } =
      createTooltipsReportEnterprise(data);

    const shownTooltip = project ? contentTooltip : contentTooltipEnterprise;

    if (data?.impacts) {
      d3.select('#heat-tooltip')
        .classed('show', isMouseOver)
        .classed('left-edge', leftEdge)
        .classed('right-edge', rightEdge)
        .style('left', `${xPosition}px`)
        .style('top', `${yPosition}px`)
        .html(data.impacts?.length > 0 ? shownTooltip : contentEmptyTooltip);
    }
  };

  useEffect(() => {
    if (shownData) {
      const svg = d3.select(svgRef.current);
      svg.selectAll('*').remove();
      MONTH_ORDER.forEach((month, index) => {
        const monthlyData = shownData[`${index + 1}`];
        const numCols = 5;

        const cellGroup = svg
          .append('g')
          .selectAll('g')
          .data(monthlyData)
          .enter()
          .append('g')
          .attr(
            'transform',
            (d, i) =>
              `translate(${index * (cellSize * numCols + spacing + 8)}, ${i * (cellSize + spacing)})`
          ); // Add padding

        if (index === 0) {
          svg
            .append('g')
            .selectAll('text')
            .data(WEEKS_ORDER)
            .enter()
            .append('text')
            .text((d, i) => {
              return `${d}`;
            })
            .attr('x', -4)
            .attr('y', (d, i) => i * (cellSize + spacing) + cellSize / 2)
            .style('text-anchor', 'end')
            .attr('alignment-baseline', 'middle')
            .style('font-size', '8px')
            .style('fill', '#7f8fa4');
        }

        svg
          .append('g')
          .selectAll('text')
          .data(DAY_NAMES)
          .enter()
          .append('text')
          .text((d) => d)
          .attr(
            'x',
            (d, i) =>
              i * (cellSize + spacing) +
              index * (cellSize * numCols + spacing + 8) +
              12
          )
          .attr('y', -4)
          .style('text-anchor', 'middle')
          .style('font-size', '8px')
          .style('fill', '#7f8fa4');

        cellGroup
          .selectAll('rect')
          .data((d) => d)
          .enter()
          .append('rect')
          .attr('x', (d, i) => (i % numCols) * (cellSize + spacing))
          .attr('y', (d, i) => Math.floor(i / numCols) * (cellSize + spacing))
          .attr('width', cellSize)
          .style('cursor', 'pointer')
          .attr('height', cellSize)
          .style('fill', (d) => {
            const { color, opacity } = getColorFill(
              d,
              reportType,
              maxNumberImpactsDay
            );
            return color;
          })
          .on('click', (d) => {
            const data = d;
            if (data.impacts && data.impacts.length > 0) {
              setIsShownImpacts(true);
              setShownImpacts(data);
              d3.select('#heat-tooltip').classed('show', false);
            }
          })
          .on('mousemove', (d) => {
            const xPosition = d3.event.pageX;
            const yPosition = d3.event.pageY;

            const rect = d3.event.target;
            rect.style.opacity = 0.35;
            if (d?.enabled) {
              handleMouseEvents(d, true, xPosition, yPosition);
            }
          })
          .on('mouseout', (d) => {
            const xPosition = d3.event.pageX;
            const yPosition = d3.event.pageY;

            const rect = d3.event.target;
            rect.style.opacity = 1;
            if (d?.enabled) {
              handleMouseEvents(d, false, xPosition, yPosition);
            }
          });

        cellGroup
          .selectAll('text')
          .data((d) => d)
          .enter()
          .append('text')
          .attr(
            'x',
            (d, i) => (i % numCols) * (cellSize + spacing) + cellSize / 2
          )
          .attr(
            'y',
            (d, i) =>
              Math.floor(i / numCols) * (cellSize + spacing) + cellSize / 2
          )
          .attr('dy', '0.35em')
          .style('text-anchor', 'middle')
          .style('fill', 'white')
          .style('cursor', 'pointer')
          .style('font-size', '10px')
          .style('font-weight', '700')
          .text((d) => {
            return d?.impacts?.length ? d.impacts.length : '';
          })
          .on('click', (d) => {
            const data = d;
            if (data?.impacts?.length > 0) {
              setIsShownImpacts(true);
              setShownImpacts(data);
              d3.select('#heat-tooltip').classed('show', false);
            }
          })
          .on('mousemove', (d) => {
            const xPosition = d3.event.pageX;
            const yPosition = d3.event.pageY;

            const rect = d3.event.target;
            rect.style.opacity = 0.35;
            if (d?.enabled) {
              handleMouseEvents(d, true, xPosition, yPosition);
            }
          })
          .on('mouseout', (d) => {
            const xPosition = d3.event.pageX;
            const yPosition = d3.event.pageY;

            const rect = d3.event.target;
            rect.style.opacity = 1;
            if (d?.enabled) {
              handleMouseEvents(d, false, xPosition, yPosition);
            }
          });

        cellGroup
          .selectAll('rect')
          .attr('stroke', 'white')
          .attr('stroke-width', 2);

        cellGroup
          .filter((d, i) => i % (numCols * 6) === 0)
          .append('text')
          .text((d, i) => MONTH_NAMES[index])
          .attr('x', () => (cellSize * numCols) / 2)
          .attr('y', -20)
          .style('text-anchor', 'middle');

        const monthNamesGroup = svg
          .append('g')
          .attr('transform', 'translate(0, -6)');
        monthNamesGroup
          .append('text')
          .attr('x', 24)
          .attr('y', -36)
          .style('text-anchor', 'middle')
          .style('fill', '#153142')
          .style('font-size', '16px')
          .text(shownDataPeriod.year);
      });
    }
  }, [shownData, reportType, size]);

  useEffect(() => {
    const getSize = () => setSize([window.innerWidth, window.innerHeight]);
    getSize();
    window.addEventListener('resize', getSize);
    return () => window.removeEventListener('resize', getSize);
  }, []);

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg
        ref={svgRef}
        style={{
          minWidth: '996px',
          overflow: 'visible',
          padding: '64px 0px 32px 8px',
        }}
        id={shownDataPeriod.year || '2023'}
      ></svg>
      <div id="heat-tooltip" />
    </div>
  );
};

export default ContributionCalendar;
